import { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuth } from './providers/AuthProvider';
import LoginPage from './pages/auth/LoginPage';
import DashboardPage from './pages/DashboardPage';
import { UsersPage, RolesPage } from './pages/admin';
import ProductsPage from './pages/products/ProductsPage';
import { TransactionsPage } from './pages/transactions';
import { PartnersPage } from './pages/partners';
import { OutletsPage } from './pages/outlets';
import { InventoryPage } from './pages/inventory';
import { ReportsPage } from './pages/reports';
import { SettingsPage } from './pages/settings';
import ComingSoonPage from './pages/ComingSoonPage';
import MainLayout from './components/layout/MainLayout';
import LoadingSpinner from './components/ui/LoadingSpinner';
import type { Permission } from './types/auth';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

interface PermissionRouteProps {
  children: React.ReactNode;
  permission: Permission;
  fallback?: React.ReactNode;
}

function PermissionRoute({ children, permission, fallback }: PermissionRouteProps) {
  const { hasPermission } = useAuth();

  if (!hasPermission(permission)) {
    if (fallback) {
      return <>{fallback}</>;
    }
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  const { i18n } = useTranslation();

  useEffect(() => {
    const dir = i18n.dir();
    document.documentElement.dir = dir;
    document.documentElement.lang = i18n.language;
  }, [i18n, i18n.language]);

  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center min-h-screen">
          <LoadingSpinner size="lg" />
        </div>
      }
    >
      <Routes>
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Routes>
                  <Route path="/" element={<DashboardPage />} />
                  <Route path="/dashboard" element={<Navigate to="/" replace />} />

                  {/* Admin Routes */}
                  <Route
                    path="/admin/users"
                    element={
                      <PermissionRoute permission="users:read">
                        <UsersPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/admin/roles"
                    element={
                      <PermissionRoute permission="roles:read">
                        <RolesPage />
                      </PermissionRoute>
                    }
                  />

                  {/* Inventory Routes */}
                  <Route
                    path="/products"
                    element={
                      <PermissionRoute permission="items:read">
                        <ProductsPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/inventory"
                    element={
                      <PermissionRoute permission="inventory:read">
                        <InventoryPage />
                      </PermissionRoute>
                    }
                  />
                  <Route path="/inventory/*" element={<ComingSoonPage />} />

                  {/* Invoice Routes */}
                  <Route
                    path="/invoices"
                    element={
                      <PermissionRoute permission="sales:read">
                        <TransactionsPage />
                      </PermissionRoute>
                    }
                  />
                  <Route path="/invoices/*" element={<ComingSoonPage />} />

                  {/* Reports */}
                  <Route
                    path="/reports"
                    element={
                      <PermissionRoute permission="reports:read">
                        <ReportsPage />
                      </PermissionRoute>
                    }
                  />
                  <Route path="/reports/*" element={<ComingSoonPage />} />

                  {/* Partners */}
                  <Route
                    path="/suppliers"
                    element={
                      <PermissionRoute permission="partners:read">
                        <PartnersPage type="supplier" />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/customers"
                    element={
                      <PermissionRoute permission="partners:read">
                        <PartnersPage type="customer" />
                      </PermissionRoute>
                    }
                  />

                  {/* Settings */}
                  <Route
                    path="/outlets"
                    element={
                      <PermissionRoute permission="outlets:read">
                        <OutletsPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <PermissionRoute permission="settings:read">
                        <SettingsPage />
                      </PermissionRoute>
                    }
                  />

                  {/* Catch all - show coming soon instead of redirect */}
                  <Route path="*" element={<ComingSoonPage />} />
                </Routes>
              </MainLayout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  );
}
